import {
  CommentsBySectionIdResponse,
  deleteQuestionLevelComment,
  makeCustomQuestionId,
  postQuestionLevelComment,
  toUserType,
  updateQuestionLevelComment,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  FeedbackCommentProps,
  FeedbackComments,
  FeedbackSection,
  FeedbackSectionId,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  getCommentsByReportId,
  markAsResolvedOrVerified,
} from '../NPOFeedback/api';
import { SuccessResponse } from '../ReportsBeta/NewReport/api';
import { useClient } from 'shared/apollo';
import {
  requestError,
  requestInit,
  RequestState,
} from 'shared/auth/hooks/useRequestState';
import {
  allFeedbackSectionsWithCustomQuestionsAndAllocations,
  FeedbackBySections,
  mapToSections,
  SelectedFeedback,
} from '../NPOFeedback/utils';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';

export const createRequestError = (message: string) => ({
  isAxiosError: false,
  name: message,
  message,
  response: {
    status: 404,
    data: {
      message,
    },
  },
});

type CommentReturnBase = {
  totalComments: number;
  commentRequestState: RequestState<
    | SuccessResponse<'newComment'>
    | SuccessResponse<'updateComment'>
    | SuccessResponse<'deleteComment'>
  >;
  commentQuestionIds: {
    [questionId: string]: boolean | undefined;
  };
  markAsResolvedRequestState: RequestState<unknown>;
  setMarkAsResolvedRequest: React.Dispatch<
    React.SetStateAction<RequestState<SuccessResponse<'answersFeedback'>>>
  >;
  deleteCommentState: (questionId: number, index: number) => unknown;
  addOrUpdateCommentState: (
    questionId: number,
    comment: FeedbackCommentProps,
    index: number | undefined,
    isCustomQuestion: boolean,
  ) => unknown;
  addOrUpdateComment: (
    questionId: number,
    comment: FeedbackCommentProps,
    sectionId: FeedbackSectionId,
    details: string | undefined,
    index: number | undefined,
    isCustomQuestion: boolean,
  ) => Promise<unknown>;
  deleteComment: (
    questionId: number,
    comment: FeedbackCommentProps,
    sectionId: FeedbackSectionId,
    index: number,
    isCustomQuestion: boolean,
  ) => unknown;
  markAsDone: (
    selectedSection: SelectedFeedback,
    feedbackStatus: FeedbackStatus,
    done: boolean,
    isCustomQuestion: boolean,
  ) => Promise<unknown>;
  commentsLoadingError: string | undefined;
};

type CommentReturn =
  | ({
      isCommentsLoading: true;
      comments: undefined;
      sections: undefined;
      feedbackBySections: undefined;
    } & CommentReturnBase)
  | ({
      isCommentsLoading: false;
      comments: FeedbackComments;
      sections: FeedbackSection[];
      feedbackBySections: FeedbackBySections;
    } & CommentReturnBase);

export const useComments = (
  reportId: string | number,
  userType: UserType,
): CommentReturn => {
  const client = useClient();
  const [comments, setComments] = useState<FeedbackComments>();
  const [totalComments, setTotalComments] = useState<number>(0);
  const [commentQuestionIds, setCommentQuestionIds] = useState<{
    [questionId: string]: boolean | undefined;
  }>({});
  const [getCommentsRequestState, makeGetCommentsRequest] = useGraphQLRequest<
    CommentsBySectionIdResponse
  >();
  const [
    markAsResolvedRequestState,
    makeMarkAsResolvedRequest,
    setMarkAsResolvedRequest,
  ] = useGraphQLRequest<SuccessResponse<'answersFeedback'>>();
  const [
    commentRequestState,
    makeCommentRequest,
    setCommentRequest,
  ] = useGraphQLRequest<
    | SuccessResponse<'newComment'>
    | SuccessResponse<'updateComment'>
    | SuccessResponse<'deleteComment'>
  >();

  useEffect(() => {
    makeGetCommentsRequest(getCommentsByReportId(client!, reportId));
  }, [reportId]);

  const addOrUpdateCommentState = useCallback(
    (
      questionId: number,
      comment: FeedbackCommentProps,
      index: number | undefined,
      isCustomQuestion: boolean,
    ) => {
      setComments(s => {
        const newState = { ...s };
        const existing = newState[questionId];
        if (!existing) {
          setTotalComments(s => s + 1);
          newState[questionId] = [comment];
        } else if (index === undefined) {
          setTotalComments(s => s + 1);
          newState[questionId] = [...existing, comment];
        } else {
          newState[questionId] = existing.map((c, i) =>
            i === index ? comment : c,
          );
        }
        return newState;
      });
    },
    [setComments],
  );

  const addOrUpdateComment = useCallback(
    async (
      questionId: number,
      comment: FeedbackCommentProps,
      sectionId: FeedbackSectionId,
      details: string | undefined,
      index: number | undefined,
      isCustomQuestion: boolean,
    ) => {
      const id = isCustomQuestion
        ? makeCustomQuestionId(questionId)
        : questionId;
      const request =
        index === undefined
          ? postQuestionLevelComment(
              client!,
              reportId,
              id,
              comment.text,
              sectionId,
              details,
            )
          : updateQuestionLevelComment(
              client!,
              comment.commentId,
              comment.text,
              reportId,
              sectionId,
            );
      makeCommentRequest(request);
      return request.then(r => {
        const result =
          index === undefined
            ? (r.data as SuccessResponse<'newComment'> | undefined)?.newComment
            : (r.data as SuccessResponse<'updateComment'> | undefined)
                ?.updateComment;
        if (result?.success) {
          const id = result.id;
          if (index === undefined) {
            setTotalComments(s => s + 1);
          }
          addOrUpdateCommentState(
            questionId,
            { ...comment, commentId: id },
            index,
            isCustomQuestion,
          );
        } else {
          setCommentRequest(
            requestError(createRequestError('Failed to add comment')),
          );
        }
        setCommentRequest(requestInit());
      });
    },
    [setComments],
  );

  const markAsDone = useCallback(
    (
      selectedSection: SelectedFeedback,
      feedbackStatus: FeedbackStatus,
      done: boolean,
      isCustomQuestion: boolean,
    ) => {
      const request = markAsResolvedOrVerified(
        client!,
        reportId,
        userType,
        selectedSection,
        feedbackStatus,
        done,
        isCustomQuestion,
      );
      makeMarkAsResolvedRequest(request);
      return request;
    },
    [reportId, client],
  );

  const deleteCommentState = useCallback(
    (questionId: number, index: number) => {
      setComments(s => {
        const newState = { ...s };
        if (newState[questionId]) {
          newState[questionId] = newState[questionId]!.filter(
            (_, i) => i !== index,
          );
        }
        return newState;
      });
      setTotalComments(s => s - 1);
    },
    [setComments, setTotalComments],
  );

  const deleteComment = useCallback(
    (
      questionId: number,
      comment: FeedbackCommentProps,
      sectionId: FeedbackSectionId,
      index: number,
    ) => {
      const request = deleteQuestionLevelComment(
        client!,
        comment.commentId,
        comment.text,
        reportId,
        sectionId,
      );
      makeCommentRequest(request);
      request.then(r => {
        if (r.data?.deleteComment.success) {
          deleteCommentState(questionId, index);
        }
      });
    },
    [deleteCommentState],
  );

  useEffect(() => {
    if (getCommentsRequestState.type !== 'REQUEST_SUCCESS') return;
    const result: FeedbackComments = {};
    setTotalComments(getCommentsRequestState.data.allNpoReportComments.length);
    for (const comment of getCommentsRequestState.data.allNpoReportComments) {
      const questionId = comment.questionId || comment.surveyQuestionId;
      if (!questionId) continue;
      setCommentQuestionIds(s => ({ ...s, [questionId]: true }));
      const c: FeedbackCommentProps = {
        userId: comment.userId,
        commentId: comment.commentId,
        details: comment.details,
        text: comment.comment,
        email: comment.userEmail,
        accountType: toUserType(comment.userType),
        name: comment.userName,
        createdAt: comment.createdAt,
      };
      const commentArray = result[questionId];
      if (commentArray) {
        commentArray.push(c);
      } else {
        result[questionId] = [c];
      }
    }
    setComments(result);
  }, [getCommentsRequestState, setComments]);

  const { sections, feedbackBySections } = useMemo(
    () =>
      mapToSections(
        getCommentsRequestState.data?.allNpoReportComments || [],
        allFeedbackSectionsWithCustomQuestionsAndAllocations,
      ),
    [getCommentsRequestState.data?.allNpoReportComments],
  );

  const error =
    getCommentsRequestState.type === 'REQUEST_ERROR'
      ? getCommentsRequestState.error.message
      : undefined;
  return comments
    ? {
        isCommentsLoading: false,
        comments,
        totalComments,
        commentRequestState,
        commentsLoadingError: error,
        sections,
        commentQuestionIds,
        feedbackBySections,
        addOrUpdateComment,
        deleteComment,
        markAsDone,
        markAsResolvedRequestState,
        setMarkAsResolvedRequest,
        deleteCommentState,
        addOrUpdateCommentState,
      }
    : {
        isCommentsLoading: true,
        comments,
        totalComments,
        commentRequestState,
        commentsLoadingError: error,
        sections: undefined,
        commentQuestionIds,
        feedbackBySections: undefined,
        markAsResolvedRequestState,
        setMarkAsResolvedRequest,
        addOrUpdateComment,
        deleteComment,
        markAsDone,
        deleteCommentState,
        addOrUpdateCommentState,
      };
};
